// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getProviderRejection,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { Info as InfoIcon } from '@/constants/hardfin-standard-icons';
import { sendReturnOrderTo3PL } from '@/return-order/api/send-to-3pl';
import { ReturnOrder } from '@/return-order/api/types';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

const UNHANDLED_3PL_ERROR_MESSAGE =
  "We're sorry but your 3PL's connection failed. Hardfin support has been automatically notified to investigate";

interface Props {
  isOpen: boolean;
  returnOrderKey: string;
  returnOrderNumber: string;
  onClose: () => void;
  onSuccess: (updatedReturnOrder: ReturnOrder) => void;
}

/**
 * Dialog component for sending a return order to third-party logistics provider.
 * Blocks dismissal while the request is in progress.
 */
export function ReturnOrderSendTo3PLDialog(props: Props) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorTitle, setErrorTitle] = useState<string | null>(null);

  const sendMutation = useMutation({
    mutationFn: () => sendReturnOrderTo3PL(props.returnOrderKey),
    onSuccess: (updatedReturnOrder) => {
      close();
      props.onSuccess(updatedReturnOrder);
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      const rejection = getProviderRejection(apiError.providerErrors);
      if (rejection !== undefined) {
        setErrorTitle(rejection.title);
        setErrorMessage(rejection.body);
        return;
      }
      setErrorTitle(null);
      const curated = getUserFacingErrorMessage(apiError);
      if (curated === SUPPORT_FALLBACK_ERROR_MESSAGE) {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        setErrorMessage(UNHANDLED_3PL_ERROR_MESSAGE);
      } else {
        setErrorMessage(curated);
      }
    },
  });

  const handleConfirm = () => {
    setErrorMessage(null);
    setErrorTitle(null);
    sendMutation.mutate();
  };

  const close = () => {
    props.onClose();
    setErrorMessage(null);
    setErrorTitle(null);
  };

  // Block dismissal while mutation is in progress
  const handleClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      if (sendMutation.isPending) {
        return; // Block dismissal during mutation
      }
    }
    close();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2,
        }}
      >
        Send to third-party fulfillment
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Alert
          severity="info"
          icon={<InfoIcon fontSize="medium" />}
          sx={{
            marginY: 3,
            alignItems: 'center',
          }}
        >
          <AlertTitle variant="h6">
            Send return order <strong>{props.returnOrderNumber}</strong> to 3PL
            provider?
          </AlertTitle>
          <Typography variant="body1">
            This will create a return order with your third-party logistics
            provider and change the order status to pending.
          </Typography>
        </Alert>

        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>
            {errorTitle && <AlertTitle>{errorTitle}</AlertTitle>}
            {errorMessage}
          </Alert>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          onClick={close}
          variant="outlined"
          disabled={sendMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={sendMutation.isPending}
          sx={{ ml: 1.5 }}
        >
          {sendMutation.isPending ? 'Sending...' : 'Send to 3PL'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
